import { createReducer } from 'redux-create-reducer'
import type { ExplorerPane, ExplorerUpload, SectionPane } from 'types/explorer'
import type { TrackId } from 'types/track'
import type { BriefId, BriefProjectId } from 'types/brief'
import { AGGREGATED_SEARCH_QUERY_SUCCESS } from '@disco/constants/ActionTypes'
import {
  BROWSE_VIEW_CACHE_CLEAR,
  EXPLORER_UPDATE,
  EXPLORER_UPLOAD_UPDATE,
  EXPLORER_UPLOADS_ADD,
  EXPLORER_UPLOADS_REMOVE,
  EXPLORER_CLOSE_PANE,
  GROUP_CREATE_SUCCESS,
  GROUP_DELETE_SUCCESS,
  GROUP_UPDATE_SUCCESS,
  LIBRARY_SELECT,
  PANE_LOAD,
  PANE_LOAD_FAIL,
  PANE_LOAD_SUCCESS,
  PANE_UPDATE,
  PANES_UPDATE,
  PANE_RECENTS_PUSH,
  PLAYBACK_PANE_UPDATE,
  BRIEF_PANE_UPDATE,
  PLAYLIST_DELETE_BATCH_SUCCESS,
  PLAYLIST_LOAD_BATCH,
  PLAYLIST_LOAD_PLAIN_UPDATE,
  PLAYLIST_VERSION_REPLACE_SUCCESS,
  SEARCH_QUERY_SUCCESS,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_LOAD_BATCH,
  TRACK_MERGE_SUCCESS,
  TRACK_PLAY,
} from '@disco/constants/ActionTypes'
import { CUSTOM_CHANNEL, TEAM_LIBRARY } from '@disco/constants/GroupTypes'
import { ALBUM, GROUP, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  BRIEF_LIST_PANE,
  PLAYLIST_PANE,
  SEARCH_RESULTS_PANE,
  TRACK_PANE,
  SECTION_LIST_PANE,
} from '@disco/constants/PaneTypes'
import {
  ARTIST_VIEW,
  DEFAULT_VIEW,
  TRACK_VIEW,
  TRACKLESS_VIEWS,
} from '@disco/constants/ViewTypes'
import { groupUrlsContainGroupIds } from '@disco/utils/groups'
import {
  getPaneInsertIndexAfterFolders,
  isReconcilableGroupPane,
} from '@disco/utils/panes'

import { BROWSE_CONTENT_CHANGES } from './browse-content-changes'

/** First page of one Browse view, as needed to show it again without a request. */
export type CachedBrowseView = {
  pane: Omit<ExplorerPane, 'results'>
  result: Partial<ExplorerPane>
  searchTerm: string
  tags: string[] | null
  // The "hide auto-created album playlists" setting the view was loaded with
  excludePlaylistTypes: string[] | null
}

type BrowseViewCache = {
  groupId: number | null
  byView: Record<string, CachedBrowseView>
}

const emptyBrowseViewCache: BrowseViewCache = { groupId: null, byView: {} }

const initialState = {
  panes: [],
  activePane: 0,
  playbackPane: null,
  uploads: [],
  browseViewCache: emptyBrowseViewCache,
}
export type State = {
  panes: (ExplorerPane | SectionPane)[]
  activePane: number
  playbackPane: any
  uploads: ExplorerUpload[]
  // STL-1248: the views already loaded in Browse, so switching back skips the request
  browseViewCache?: BrowseViewCache
}

/** The Browse (team library) pane, the only one whose views are cached. */
export const isBrowseLibraryPane = (
  pane: Partial<ExplorerPane> | null | undefined,
): boolean =>
  !!pane &&
  pane.index === 0 &&
  pane.type === SEARCH_RESULTS_PANE &&
  pane.groupType === TEAM_LIBRARY

const BROWSE_VIEW_CACHE_RESETS = [
  BROWSE_VIEW_CACHE_CLEAR,
  LIBRARY_SELECT,
  ...BROWSE_CONTENT_CHANGES,
]

// Load batches and pane updates change the Browse list on screen without a
// pane load: drag-moves, finished uploads, websocket updates, a saved playlist
// moving to the top. Unchanged rows keep their identity, so any difference
// means new contents.
const browseRowsChanged = (
  before: State,
  after: State,
  { ignoreAppendedPage = false } = {},
): boolean => {
  const current = after.panes[0] as ExplorerPane | undefined
  // A pane that is loading is about to be replaced by the response anyway
  if (!isBrowseLibraryPane(current) || !current?.loaded || current.loading)
    return false
  const previousRows = (before.panes[0] as ExplorerPane | undefined)?.results
  const rows = current.results
  if (!previousRows || !rows) return previousRows !== rows
  const keptPrefix = previousRows.every((row, index) => row === rows[index])
  // A next page only appends; the first page the cache holds is unchanged
  if (ignoreAppendedPage && keptPrefix) return false
  return previousRows.length !== rows.length || !keptPrefix
}

const withBrowseViewCache =
  (
    reducer: (state: State, action: any) => State,
  ): ((state: State, action: any) => State) =>
  (state, action) => {
    const next = reducer(state, action)

    const resetsCache =
      BROWSE_VIEW_CACHE_RESETS.includes(action.type) ||
      ([
        TRACK_LOAD_BATCH,
        PLAYLIST_LOAD_BATCH,
        PLAYLIST_LOAD_PLAIN_UPDATE,
        PANE_UPDATE,
      ].includes(action.type) &&
        browseRowsChanged(state, next, {
          // Pagination also goes through PANE_UPDATE
          ignoreAppendedPage: action.type === PANE_UPDATE,
        }))
    if (resetsCache) {
      return next.browseViewCache === emptyBrowseViewCache
        ? next
        : { ...next, browseViewCache: emptyBrowseViewCache }
    }

    // Opt-in, so lists built for another purpose (a deep-linked playlist
    // pinned to the top) are never restored as a view. A stale response
    // leaves the state untouched and is not cached either.
    if (
      action.type !== PANE_LOAD_SUCCESS ||
      !action.cacheBrowseView ||
      next === state ||
      !isBrowseLibraryPane(action.pane)
    ) {
      return next
    }

    // The pane still carries the previous view's rows; the result replaces them
    const { results: _previousRows, ...pane } = action.pane
    const cache = next.browseViewCache ?? emptyBrowseViewCache
    return {
      ...next,
      browseViewCache: {
        groupId: pane.groupId,
        byView: {
          ...(cache.groupId === pane.groupId ? cache.byView : {}),
          [pane.view || DEFAULT_VIEW]: {
            pane,
            result: action.result,
            searchTerm: pane.searchTerm ?? '',
            tags: action.tags ?? null,
            excludePlaylistTypes: action.excludePlaylistTypes ?? null,
          },
        },
      },
    }
  }

/**
 * Gets the group URLs of the playlists a track is in. A library's track view
 * also lists the tracks of its playlists, so these count as the track's groups.
 * @param track - The track, with its playlist versions
 * @returns The group URLs of the track's playlists
 */
export function getExtendedTrackGroupUrls(track): string[] {
  if (!track.playlist_versions) return []
  const groupUrls: string[] = []
  track.playlist_versions.forEach(playlist => {
    groupUrls.push(...playlist.groups)
  })
  return groupUrls
}

function findItemPaneIndex(state, item) {
  return state.panes.findIndex(p => {
    switch (item.type) {
      case PLAYLIST:
        return p.type === PLAYLIST_PANE && p.playlist_id === item.playlist_id

      case TRACK:
        return p.type === TRACK_PANE && p.id === item.id

      case 'brief':
        return p.type === SEARCH_RESULTS_PANE && p.meta?.briefId === item.id

      case 'brief_project':
        return p.type === BRIEF_LIST_PANE && p.meta?.briefProjectId === item.id

      default:
        return false
    }
  })
}

function closePane(state, index) {
  return {
    ...state,
    panes: state.panes.filter(p => p.index < index),
    activePane: index > 0 ? index - 1 : 0,
  }
}

function getSortedPaneIndexes(state, items) {
  return items
    .map(item => {
      return {
        item: item,
        index: findItemPaneIndex(state, item),
      }
    })
    .filter(itemPane => itemPane.index > 0)
    .sort((a, b) => a.index - b.index)
}

// function checks if we need to close pane because it's no longer belongs
// to results in previous pane
function closePaneOnItemsChange(state, items) {
  // get sorted by pane index items
  const itemPaneIndexes = getSortedPaneIndexes(state, items)

  // check we need to close something
  for (let i = 0; i < itemPaneIndexes.length; i++) {
    const { item, index } = itemPaneIndexes[i]
    const previousPane = state.panes[index - 1]
    const removedFromGroup =
      previousPane.type === SEARCH_RESULTS_PANE &&
      (previousPane.groupId || previousPane.groupIds) &&
      !groupUrlsContainGroupIds(item.groups, [
        previousPane.groupId,
        ...(previousPane.groupIds || []),
      ])
    if (removedFromGroup) return closePane(state, index)
  }

  return state
}

// function checks if we need to close the pane if its item was deleted
function closePaneOnItemsDelete(state, items) {
  const itemPaneIndexes = getSortedPaneIndexes(state, items)
  if (itemPaneIndexes.length > 0)
    return closePane(state, itemPaneIndexes[0].index)
  return state
}

function updateSearchResultsPaneOnPlaylistChange(playlists, p) {
  if (
    p.type !== SEARCH_RESULTS_PANE ||
    !p.groupId ||
    !p.results ||
    [ARTIST_VIEW, TRACK_VIEW].includes(p.view)
  )
    return p
  const playlistsToRemove: any[] = []
  const playlistsToAdd: any[] = []
  playlists.forEach(playlist => {
    const playlistExists = (p.results ?? []).some(item => {
      return item.type === PLAYLIST && item.playlist_id === playlist.playlist_id
    })
    const { groups } = playlist
    const playlistInGroup = groupUrlsContainGroupIds(groups, [
      p.groupId,
      ...(p.groupIds || []),
    ])

    if (!playlistInGroup && playlistExists) {
      playlistsToRemove.push(playlist)
    } else if (playlistInGroup && !playlistExists) {
      playlistsToAdd.push(playlist)
    }
  })
  const clearedResults = (p.results ?? []).filter(item => {
    return (
      item.type !== PLAYLIST ||
      !playlistsToRemove.find(
        playlist => playlist.playlist_id === item.playlist_id,
      )
    )
  })
  const insertIndex = clearedResults.filter(
    item => item.type === PLAYLIST || item.type === GROUP,
  ).length
  // TODO: refactor activeItem splitting
  const activeItemIndex = Number(p.activeItem?.split('/')?.[1] || 0)
  let activeItemId = p.activeItem

  if (p.activeItem !== undefined && p.results) {
    activeItemId = String(p.results[activeItemIndex]?.id)
  }

  return {
    ...p,
    activeItemId,
    results: [
      ...clearedResults.slice(0, insertIndex),
      ...playlistsToAdd,
      ...clearedResults.slice(insertIndex),
    ],
  }
}

// updates tracks and track info in pane, can be dropped once we get rid of
// stashed threads, as they save whole info about panes
function updatePlaylistPaneOnPlaylistChange(playlist, p) {
  if (
    p.type !== PLAYLIST_PANE ||
    p.loading ||
    p.playlist_id !== playlist.playlist_id
  ) {
    return p
  }

  if (
    p.tracks_info &&
    playlist.tracks_info &&
    (p.tracks_info.next !== playlist.tracks_info.next ||
      (p.tracks ?? []).length !== playlist.tracks.length)
  ) {
    const tracks = playlist.tracks.map(track => {
      return track.id ? track.id : track
    })
    return { ...p, ...playlist, tracks: tracks }
  }

  return p
}

function closePaneOnGroupChange(state, group) {
  // close group pane if group was deleted
  const paneIndex = state.panes.findIndex(p => {
    return p.type === SEARCH_RESULTS_PANE && p.groupId === group.id
  })
  return paneIndex > -1 ? closePane(state, paneIndex) : state
}

function handlePlaylistChange(state, playlists) {
  // if panes are empty do nothing
  if (!state.panes) return state
  // handle only fully loaded playlists
  const loadedPlaylists = playlists.filter(playlist => playlist.groups)
  const resultState = closePaneOnItemsChange(state, playlists)
  const resultPanes = resultState.panes.map(p => {
    if (p.type === SEARCH_RESULTS_PANE) {
      return updateSearchResultsPaneOnPlaylistChange(loadedPlaylists, p)
    } else if (p.type === PLAYLIST_PANE) {
      const updatedPlaylist = loadedPlaylists.find(
        playlist => playlist.playlist_id === p.playlist_id,
      )

      if (updatedPlaylist) {
        return updatePlaylistPaneOnPlaylistChange(updatedPlaylist, p)
      }
    }

    return p
  })
  return { ...resultState, panes: resultPanes }
}

function handleAggregatedSearch(state, action) {
  const resultPanes = state.panes.map(p => {
    // a channel pane is the same type but answers a url of its own (see
    // `isStaleGroupResponse`); only a real search pane holds MDS results, so
    // a late aggregated response cannot leak into the channel on screen
    if (p.type !== SEARCH_RESULTS_PANE || p.url) return p
    return {
      ...p,
      aggregatedSearchResults: {
        searchResults: action.params.aggregatedResults,
        next: action.params.aggregatedNextPageUrl,
      },
    }
  })
  return { ...state, panes: resultPanes }
}

function handleTracksChange(
  state,
  tracks,
  { reconcileGroupPanes = false } = {},
) {
  // if panes are empty do nothing
  if (!state.panes) return state
  // handle only fully loaded tracks
  const loadedTracks = tracks.filter(track => track.groups)
  const resultState = closePaneOnItemsChange(state, loadedTracks)
  const resultPanes = resultState.panes.map(p => {
    // we had to take care only about "search results"
    if (
      p.type !== SEARCH_RESULTS_PANE ||
      !p.groupId ||
      !p.results ||
      TRACKLESS_VIEWS.includes(p.view)
    )
      return p
    if (reconcileGroupPanes) {
      // The user just changed group membership (e.g. drag-moved tracks), so
      // reconcile the open channel/folder columns they are looking at. The
      // predicate is shared with the pane's upload rows, so an "Uploading"
      // row can only appear where its finished track row will land.
      if (!isReconcilableGroupPane(p)) return p
    } else {
      if (p.groupType === CUSTOM_CHANNEL) {
        // Passive batch loads (websocket track updates, lyrics or territory
        // saves) leave channel/folder columns alone.
        return p
      }
      // only custom channels can have tracks in default view
      if (!p.view || p.view === DEFAULT_VIEW) return p
    }
    const tracksToRemove: any[] = []
    const tracksToAdd: any[] = []
    loadedTracks.forEach(loadedTrack => {
      const trackExists = (p.results ?? []).some(
        item => item.type === TRACK && item.id === loadedTrack.id,
      )
      const trackInGroup = groupUrlsContainGroupIds(loadedTrack.groups, [
        p.groupId,
        ...(p.groupIds || []),
      ])

      if (trackExists && !trackInGroup) {
        // special treatment for track view as it may contain tracks that
        // don't have group id set but belong to playlist that belongs to chosen
        // group
        if (p.view === TRACK_VIEW && loadedTrack.playlist_versions) {
          const groupUrls = getExtendedTrackGroupUrls(loadedTrack)

          if (
            !groupUrlsContainGroupIds(groupUrls, [
              p.groupId,
              ...(p.groupIds || []),
            ])
          ) {
            tracksToRemove.push(loadedTrack)
          }
        } else {
          tracksToRemove.push(loadedTrack)
        }
      } else if (!trackExists && trackInGroup) {
        tracksToAdd.push(loadedTrack)
      }
    })
    const keptResults = (p.results ?? []).filter(item => {
      return (
        item.type !== TRACK ||
        !tracksToRemove.find(track => track.id === item.id)
      )
    })
    // Added tracks go where the pane's upload rows sit, right after the
    // folders, so a row keeps its place when its upload finishes.
    const insertAt = getPaneInsertIndexAfterFolders(keptResults)
    return {
      ...p,
      results: [
        ...keptResults.slice(0, insertAt),
        ...tracksToAdd,
        ...keptResults.slice(insertAt),
      ],
    }
  })
  return { ...resultState, panes: resultPanes }
}

const removeTracksFromSearchPane = (pane: ExplorerPane, trackIds) => ({
  ...pane,
  results: (pane.results ?? []).filter(item => {
    return item.type !== TRACK || trackIds.indexOf(item.id) === -1
  }),
})

function closePanesOnTracksRemoval(state, trackIds) {
  // close track pane if track was deleted
  const resultState = closePaneOnItemsDelete(
    state,
    trackIds.map(id => {
      // closePaneOnItemsDelete expects object, not simple id
      return {
        id: id,
        type: TRACK,
        groups: [],
      }
    }),
  )
  // special treatment for searchResults, they need further refactoring
  const resultPanes = resultState.panes.map(p => {
    if (p.type === SEARCH_RESULTS_PANE)
      return removeTracksFromSearchPane(p, trackIds)

    if (p.type === PLAYLIST_PANE) {
      return {
        ...p,
        tracks: (p.tracks ?? []).filter(
          trackId => trackIds.indexOf(trackId) === -1,
        ),
      }
    }

    if (p.type === SECTION_LIST_PANE)
      return {
        ...p,
        sections: (p.sections ?? []).map(section => {
          return {
            ...section,
            data: (section.data ?? []).filter(item => {
              return item.type !== TRACK || !trackIds.includes(item.id)
            }),
          }
        }),
      }
    return p
  })
  return {
    ...resultState,
    panes: resultPanes,
    playbackPane: resultState.playbackPane
      ? removeTracksFromSearchPane(resultState.playbackPane, trackIds)
      : null,
  }
}

const closePaneOnPlaylistChange = (state, playlistIds, deleted) => {
  // close playlist pane if playlist was deleted
  const resultState = closePaneOnItemsDelete(
    state,
    playlistIds.map(id => {
      // closePaneOnItemsDelete expects object, not simple id
      return {
        playlist_id: id,
        type: PLAYLIST,
      }
    }),
  )
  // if deleted remove playlists from the search results list
  const resultPanes = deleted
    ? resultState.panes.map(p => {
        if (p.type === SEARCH_RESULTS_PANE)
          return {
            ...p,
            results: (p.results ?? []).filter(item => {
              return (
                item.type !== PLAYLIST ||
                playlistIds.indexOf(item.playlist_id) === -1
              )
            }),
          }
        if (p.type === SECTION_LIST_PANE)
          return {
            ...p,
            sections: (p.sections ?? []).map(section => {
              return {
                ...section,
                data: (section.data ?? []).filter(item => {
                  return (
                    item.type !== PLAYLIST || !playlistIds.includes(item.id)
                  )
                }),
              }
            }),
          }
        return p
      })
    : resultState.panes
  return { ...resultState, panes: resultPanes }
}

function closePanesOnBriefRemoval(state, briefId: BriefId) {
  const resultState = closePaneOnItemsDelete(
    state,
    [briefId].map(id => {
      // closePaneOnItemsDelete expects object, not simple id
      return {
        id: id,
        type: 'brief',
      }
    }),
  )

  const resultPanes = resultState.panes.map(p => {
    if (p.type === BRIEF_LIST_PANE) {
      return {
        ...p,
        results: (p.results ?? []).filter(item => {
          return (
            item.type !== 'brief' ||
            (item.type === 'brief' && item.id !== briefId)
          )
        }),
      }
    }
    return p
  })
  return { ...state, panes: resultPanes }
}

function closePanesOnBriefProjectRemoval(
  state,
  briefProjectId: BriefProjectId,
) {
  const resultState = closePaneOnItemsDelete(
    state,
    [briefProjectId].map(id => {
      return {
        id: id,
        type: 'brief_project',
      }
    }),
  )

  const resultPanes = resultState.panes.map(p => {
    if (p.type === BRIEF_LIST_PANE) {
      return {
        ...p,
        results: (p.results ?? []).filter(item => {
          return item.type !== 'brief_project' || item.id !== briefProjectId
        }),
      }
    }
    return p
  })
  return { ...resultState, panes: resultPanes }
}

/**
 * Value of the `X-Disco-GroupIDs` request header for a pane, or '' when the
 * pane isn't channel scoped.
 *
 * Every channel pane shares the same URL (`/api/browse/`) and identifies its
 * channel(s) through this header instead, so the header value is also the only
 * way to tell which channel a pane's response belongs to.
 * `isStaleGroupResponse` depends on both sides deriving from this one builder;
 * a separate reimplementation could drift and silently disable the guard.
 */
export const getPaneGroupHeader = (pane: Partial<ExplorerPane>): string => {
  if (pane.groupIds) return JSON.stringify(pane.groupIds)
  if (pane.groupId) return JSON.stringify([pane.groupId])
  return ''
}

/**
 * True when the response belongs to a different channel than the one now
 * occupying that pane slot - i.e. the user switched channels while this
 * request was in flight, so applying it would restore the channel they left.
 */
function isStaleGroupResponse(
  state: State,
  pane: Partial<ExplorerPane>,
): boolean {
  // `loadPaneWithSearchResults` hands the store's current results to
  // the slot rather than answering a request made for it, so it carries no url
  // of its own and its `groupIds` are the search's group filter, not the
  // channel the slot is showing. Comparing those dropped the results of every
  // group-filtered search until an unfiltered pane load replaced them.
  if (pane.type === SEARCH_RESULTS_PANE && !pane.url) return false

  const existingPane =
    pane.index === undefined ? undefined : state.panes[pane.index]
  if (!existingPane || existingPane.type !== pane.type) return false
  const existingGroupKey = getPaneGroupHeader(existingPane)
  const incomingGroupKey = getPaneGroupHeader(pane)
  // an empty header means "not channel scoped", which is not comparable
  return (
    existingGroupKey !== '' &&
    incomingGroupKey !== '' &&
    existingGroupKey !== incomingGroupKey
  )
}

function updatePaneOnBriefProjectCreation(state, briefProject) {
  const panes = state.panes.map(p => {
    if (p.index > 0 || p.type !== BRIEF_LIST_PANE || !p.results) {
      return p
    }

    return {
      ...p,
      results: [
        {
          ...briefProject,
          type: 'brief_project',
        },
        ...p.results,
      ],
    }
  })

  return { ...state, panes }
}

export const initReducer: (
  prefix?: string,
) => (state: State, action: any) => State = (prefix: string = '') =>
  createReducer<State, any>(initialState, {
    [`${prefix}${PANE_LOAD}`](state, action) {
      const { pane, view } = action
      return {
        ...state,
        panes: [
          ...state.panes.slice(0, pane.index),
          { ...pane, loading: true, loaded: false, view },
        ],
        activePane: pane.index,
      }
    },

    [`${prefix}${PANE_LOAD_SUCCESS}`](state, action) {
      const { pane, result } = action

      // prevent overriding existing pane with results from previous
      // long running request
      if (isStaleGroupResponse(state, pane)) return state

      if (pane.index < state.panes.length) {
        const existingPane = state.panes[pane.index] as ExplorerPane
        const isSearchResultsPane = existingPane.type === SEARCH_RESULTS_PANE
        const groupIsTheSame = existingPane.groupId === pane.groupId
        const isBriefsPane = existingPane.type === BRIEF_LIST_PANE

        const viewSwitch =
          existingPane.type === pane.type &&
          ((isSearchResultsPane && groupIsTheSame) || isBriefsPane)

        // For brief inbox panes: prevent race conditions when a stale API response
        if (
          pane.groupType === 'brief_inbox' &&
          result &&
          existingPane.meta?.briefId !== undefined &&
          pane.meta?.briefId !== undefined &&
          existingPane.meta?.briefId !== pane.meta?.briefId
        ) {
          return state
        } else if (pane.groupType !== 'brief_inbox') {
          // For non-brief panes: use the existing URL check
          // skip check for search query results (url is undefined)
          // skip check for view switch
          // compare panes of the same type only
          if (
            !viewSwitch &&
            pane.url &&
            existingPane.type === pane.type &&
            existingPane.url !== pane.url
          ) {
            return state
          }
        }
      }

      let newPane = {
        ...pane,
        ...result,
        loading: false,
        loaded: true,
        error: null,
        // A pin (WOOF-254) belongs to the list it was added to, not a reload
        pinnedItem: undefined,
      }

      // If this pane has a activateByPlaylistId in meta, find and highlight that playlist
      if (pane.meta?.activateByPlaylistId && result.results) {
        const playlistIndex = result.results.findIndex(
          (item: any) =>
            item.type === 'playlist' &&
            item.playlist_id === pane.meta.activateByPlaylistId,
        )

        if (playlistIndex !== -1) {
          const itemPrefix = pane.view || 'pane'
          // Remove activateByPlaylistId from meta after using it
          const { activateByPlaylistId, ...remainingMeta } = pane.meta || {}
          newPane = {
            ...newPane,
            activeItem: `${itemPrefix}/${playlistIndex}`,
            meta: remainingMeta,
          }
        }
      }

      return { ...state, panes: [...state.panes.slice(0, pane.index), newPane] }
    },

    [`${prefix}${PANE_LOAD_FAIL}`](state, action) {
      const { pane, error } = action

      if (isStaleGroupResponse(state, pane)) return state

      const newPane = { ...pane, loading: false, loaded: true, error: error }
      return {
        ...state,
        panes: [
          ...state.panes.slice(0, pane.index),
          newPane,
          ...state.panes.slice(pane.index + 1),
        ],
      }
    },

    [`${prefix}${PANE_UPDATE}`](state, action) {
      const { result } = action

      // a pagination page from a channel the user already switched away from
      // must not merge into the pane that replaced it; in-channel updates
      // (renames, sorts, activeItem) carry the same group and pass through
      if (isStaleGroupResponse(state, result)) return state

      const newPane = { ...state.panes[result.index], ...result }
      return {
        ...state,
        panes: [
          ...state.panes.slice(0, result.index),
          newPane,
          ...state.panes.slice(result.index + 1),
        ],
      }
    },

    [`${prefix}${PANES_UPDATE}`](state, action) {
      let panes = state.panes
      action.results.forEach(result => {
        const newPane = { ...state.panes[result.index], ...result }
        panes = [
          ...panes.slice(0, result.index),
          newPane,
          ...panes.slice(result.index + 1),
        ]
      })
      return { ...state, panes }
    },

    [`${prefix}${PANE_RECENTS_PUSH}`](state, action) {
      const { item, itemType } = action
      const recentsPane = state.panes?.find(
        pane => pane.type === SECTION_LIST_PANE,
      ) as SectionPane

      if (!recentsPane?.sections) {
        return state
      }

      const sectionTitleMap = {
        [TRACK]: 'Tracks',
        [PLAYLIST]: 'Playlists',
        [GROUP]: 'Channels',
        [ALBUM]: 'Albums',
      }

      const sectionTitle = sectionTitleMap[itemType]

      if (!sectionTitle) {
        return state
      }

      const itemId = itemType === PLAYLIST ? item.playlist_id : item.id
      const section = recentsPane.sections.find(
        section => section.title === sectionTitle,
      )

      if (!section) {
        return state
      }

      const existingItemIndex = section.data.findIndex(
        (dataItem: any) => dataItem.type === itemType && dataItem.id === itemId,
      )

      const newItem = {
        type: itemType,
        id: itemId,
      }

      let updatedData
      if (existingItemIndex >= 0) {
        updatedData = [
          newItem,
          ...section.data.filter((_, index) => index !== existingItemIndex),
        ]
      } else {
        updatedData = [newItem, ...section.data]
      }

      if (updatedData.length > 3) {
        updatedData = updatedData.slice(0, 3)
      }

      const updatedSections = recentsPane.sections.map(sectionItem =>
        sectionItem.title === sectionTitle
          ? { ...sectionItem, data: updatedData }
          : sectionItem,
      )

      const recentsIndex = state.panes.findIndex(
        pane => pane.type === SECTION_LIST_PANE,
      )

      const updatedPanes = [
        ...state.panes.slice(0, recentsIndex),
        {
          ...recentsPane,
          sections: updatedSections,
        },
        ...state.panes.slice(recentsIndex + 1),
      ]

      return { ...state, panes: updatedPanes }
    },

    [`${prefix}${EXPLORER_UPDATE}`](state, action) {
      const { result } = action
      return { ...state, ...result }
    },

    [PLAYLIST_DELETE_BATCH_SUCCESS](state, { playlistIds }) {
      return closePaneOnPlaylistChange(state, playlistIds, true)
    },

    [PLAYLIST_VERSION_REPLACE_SUCCESS](state, { replacedPlaylists }) {
      return closePaneOnPlaylistChange(
        state,
        replacedPlaylists.map(p => p.playlist_id),
        false,
      )
    },

    [TRACK_DELETE_BATCH_SUCCESS](
      state,
      {
        trackIds,
      }: {
        trackIds: TrackId[]
      },
    ) {
      return closePanesOnTracksRemoval(state, trackIds)
    },

    [TRACK_MERGE_SUCCESS](state, { tracksToMerge }) {
      const trackIds = tracksToMerge.map(t => t.id)
      return closePanesOnTracksRemoval(state, trackIds)
    },

    [GROUP_CREATE_SUCCESS](state, action) {
      // special treatment for searchResults, they need further refactoring
      const panes: ExplorerPane[] = (state.panes as ExplorerPane[]).map(p => {
        if (
          p.type !== SEARCH_RESULTS_PANE ||
          p.groupId !== action.group.parent
        ) {
          return p
        }

        const insertIndex = (p.results ?? []).filter(
          item => item.type === GROUP,
        ).length
        return {
          ...p,
          results: [
            ...(p.results ?? []).slice(0, insertIndex),
            action.group,
            ...(p.results ?? []).slice(insertIndex),
          ],
        }
      })
      return { ...state, panes: panes }
    },

    [GROUP_DELETE_SUCCESS](state, action) {
      const resultState = closePaneOnGroupChange(state, action.group)
      // special treatment for searchResults, they need further refactoring
      const resultPanes = resultState.panes.map(p => {
        if (p.type !== SEARCH_RESULTS_PANE) return p
        return {
          ...p,
          results: (p.results ?? []).filter(item => {
            return item.type !== GROUP || item.id !== action.group.id
          }),
        }
      })
      return { ...resultState, panes: resultPanes }
    },

    [GROUP_UPDATE_SUCCESS](state, action) {
      const resultState = action.parentChanged
        ? closePaneOnGroupChange(state, action.group)
        : state
      const resultPanes: ExplorerPane[] = resultState.panes.map(p => {
        if (p.type !== SEARCH_RESULTS_PANE) return p

        // pane name is a snapshot from open time; refresh so renames show without reopening
        if (p.groupId === action.group.id) {
          return { ...p, name: action.group.name }
        }

        if (p.groupId !== action.group.parent) {
          // remove group if it will be found in results
          return {
            ...p,
            results: (p.results ?? []).filter(item => {
              return item.type !== GROUP || item.id !== action.group.id
            }),
          }
        } else {
          // if it already exist in results, do nothing
          const groupExists = (p.results ?? []).find(item => {
            return item.type !== GROUP || item.id === action.group.id
          })
          if (groupExists) return p
          const insertIndex = (p.results ?? []).filter(
            item => item.type === GROUP,
          ).length
          return {
            ...p,
            results: [
              ...(p.results ?? []).slice(0, insertIndex),
              action.group,
              ...(p.results ?? []).slice(insertIndex),
            ],
          }
        }
      })
      return { ...resultState, panes: resultPanes }
    },

    [PLAYLIST_LOAD_PLAIN_UPDATE](state, { playlists }) {
      return handlePlaylistChange(state, playlists)
    },

    [PLAYLIST_LOAD_BATCH](state, action) {
      // handle only single playlist updates, if multiple playlists loaded
      // this means it's not save operation, revisit when we have multiple
      // item operations
      if (action.list && action.list.length > 0) {
        return handlePlaylistChange(state, action.list)
      }

      return state
    },

    [TRACK_LOAD_BATCH](state, action) {
      if (action.list && action.list.length > 0) {
        return handleTracksChange(state, action.list, {
          reconcileGroupPanes: action.reconcileGroupPanes,
        })
      }

      return state
    },

    [SEARCH_QUERY_SUCCESS](state) {
      return { ...state, activePane: 0 }
    },

    [AGGREGATED_SEARCH_QUERY_SUCCESS](state, action) {
      return handleAggregatedSearch(state, action)
    },

    // TODO: playback pane doesn't really belong to explorer reducer and should be
    // moved to media reducer, especially with the fact that in mobile app
    // several explorer reducers exists and user must be able to play tracks
    // from any of them, as a workaround give a way to pass the playback pane
    // as param, after merge with the web app, move playbackPane to media reducer
    [`${prefix}${TRACK_PLAY}`](state, { playbackPane }) {
      return playbackPane?.type === SEARCH_RESULTS_PANE
        ? { ...state, playbackPane }
        : { ...state, playbackPane: null }
    },

    [`${prefix}${PLAYBACK_PANE_UPDATE}`](state, { pane }) {
      return { ...state, playbackPane: pane }
    },

    [`${prefix}${BRIEF_PANE_UPDATE}`](state, { brief }) {
      const updatedPanes = state.panes.map(pane => {
        if (pane.type !== BRIEF_LIST_PANE || !pane.results) return pane

        const hasUpdatedBrief = pane.results.some(
          (item: any) => item.type === 'brief' && item.id === brief.id,
        )

        if (hasUpdatedBrief) {
          const updatedResults = pane.results.map((item: any) => {
            if (item.type === 'brief' && item.id === brief.id) {
              return {
                ...item,
                submission_status: brief.submission_status,
                deadline: brief.deadline,
                deadline_timezone: brief.deadline_timezone,
              }
            }
            return item
          })

          return {
            ...pane,
            results: updatedResults,
          }
        }

        return pane
      })

      return { ...state, panes: updatedPanes }
    },

    [EXPLORER_UPLOADS_ADD](state, { uploads }) {
      return { ...state, uploads: [...state.uploads, ...uploads] }
    },

    [EXPLORER_UPLOAD_UPDATE](state, { tempId, patch }) {
      const target = state.uploads.find(upload => upload.tempId === tempId)
      // Progress arrives on every XHR chunk, far more often than the rounded
      // percentage changes. Returning the same state keeps those repeats from
      // notifying subscribers at all — every connected component in the app
      // re-runs its selectors on a store change.
      const changesNothing =
        !target || Object.keys(patch).every(key => target[key] === patch[key])
      if (changesNothing) return state

      return {
        ...state,
        uploads: state.uploads.map(upload =>
          upload.tempId === tempId ? { ...upload, ...patch } : upload,
        ),
      }
    },

    [EXPLORER_UPLOADS_REMOVE](state, { ids }) {
      return {
        ...state,
        uploads: state.uploads.filter(upload => !ids.includes(upload.tempId)),
      }
    },

    [`${prefix}${EXPLORER_CLOSE_PANE}`](state, { index }) {
      return closePane(state, index)
    },

    ['briefs/deleteBrief/fulfilled'](state, action) {
      const briefId = action.meta.arg
      return closePanesOnBriefRemoval(state, briefId)
    },
    ['briefProjects/deleteBriefProject/fulfilled'](state, action) {
      const briefProjectId = action.meta.arg
      return closePanesOnBriefProjectRemoval(state, briefProjectId)
    },
    ['briefProjects/createBriefProject/fulfilled'](state, action) {
      const briefProject = action.payload
      return updatePaneOnBriefProjectCreation(state, briefProject)
    },
  })
export const explorerReducer = withBrowseViewCache(initReducer())
